import React, { useState, useMemo } from 'react';
import { T, addDays, isoDate, parseDate, todayIso, startOfWeek } from './bookingsTheme';

// ─── Month calendar ──────────────────────────────────────────────────────────
//
// Sits on the right of the booking screen, the way Team Today's does. Its job
// is jumping to a week that is not adjacent to this one, which the previous
// and next arrows make tedious.
//
// Dates outside the booking window are rendered disabled rather than hidden, so
// the limit is visible: you can see that the 3rd of next month exists and is
// simply not open yet, instead of wondering where it went.

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function MiniCalendar({ selected, onSelect, maxDate, minDate, marked = {} }) {
  const [cursor, setCursor] = useState(() => parseDate(selected || todayIso()));
  const today = todayIso();

  const { cells, monthLabel } = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    // Grid starts on the Monday of the week containing the 1st.
    const gridStart = startOfWeek(isoDate(first));
    const out = [];
    for (let i = 0; i < 42; i++) {
      const iso = addDays(gridStart, i);
      const d = parseDate(iso);
      out.push({ iso, day: d.getDate(), inMonth: d.getMonth() === month });
    }
    // Drop a trailing all-blank week rather than always drawing six rows.
    const trimmed = out.slice(0, out.slice(35).some(c => c.inMonth) ? 42 : 35);
    return {
      cells: trimmed,
      monthLabel: first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    };
  }, [cursor]);

  const step = (n) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const selectedWeek = selected ? startOfWeek(selected) : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className={`${T.strong} text-gray-800`}>{monthLabel}</span>
        <div className="flex gap-1">
          <button type="button" onClick={() => step(-1)} aria-label="Previous month"
            className="w-6 h-6 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 leading-none">‹</button>
          <button type="button" onClick={() => step(1)} aria-label="Next month"
            className="w-6 h-6 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 leading-none">›</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-y-1">
        {DOW.map((d, i) => (
          <div key={i} className={`${T.meta} font-bold text-gray-400 text-center`}>{d}</div>
        ))}
        {cells.map(c => {
          const tooLate = maxDate && c.iso > maxDate;
          const tooEarly = minDate && c.iso < minDate;
          const disabled = tooLate || tooEarly;
          const isSelected = c.iso === selected;
          // The whole selected week is tinted, because the screen behind this
          // shows a week: highlighting one day would misrepresent what is shown.
          const inSelectedWeek = selectedWeek && startOfWeek(c.iso) === selectedWeek;
          return (
            <button
              key={c.iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect?.(c.iso)}
              title={disabled ? (tooLate ? 'Beyond the booking window' : 'In the past') : c.iso}
              className={`h-7 w-full rounded-full ${T.body} tabular-nums transition-colors relative
                ${isSelected ? 'bg-navy text-white font-bold'
                  : inSelectedWeek && !disabled ? 'bg-navy/10 text-navy font-semibold'
                  : disabled ? 'text-gray-300 cursor-not-allowed'
                  : c.inMonth ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100'}
                ${c.iso === today && !isSelected ? 'ring-1 ring-blue-400' : ''}`}
            >
              {c.day}
              {marked[c.iso] && !isSelected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-emerald-500" />
              )}
            </button>
          );
        })}
      </div>

      {maxDate && (
        <p className={`${T.meta} text-gray-500 mt-2 pt-2 border-t border-gray-100`}>
          Bookable up to {parseDate(maxDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.
        </p>
      )}
    </div>
  );
}
